// Third Party
import dayjs from '@utils/dayjs-setup';

/**
 * This file contains several date helper functions that work with and return the Date object instead of a moment.Moment object.
 *
 * As a general rule, any function that returns a Date object(s) should expect Date objects as input and output.
 *
 * Alternatively, any function that provides utility to a Date object(s) can use the dayjs library. E.g. formatDateMMMD
 *
 * Finally, there is a lot of type checking in this module. This is because graphql codegen implements Date and DateTime scalars as strings.
 */

/**
 * Asserts that the input is a Date object. If the input is not a Date object, an error is thrown.
 */
export function assertIsDate(date: unknown): asserts date is Date {
  if (!(date instanceof Date)) {
    throw new Error('Input is not a Date object');
  }
}

/**
 * This function takes in two Date objects, startDate and endDate,
 * and returns an array of strings representing each individual date within that range.
 */
export function getDatesWithinRange(
  startDate: string,
  endDate: string
): string[] {
  const dates: string[] = [];
  let currentDate = dayjs(startDate);

  while (currentDate.isSameOrBefore(endDate)) {
    dates.push(currentDate.format('YYYY-MM-DD'));
    currentDate = currentDate.add(1, 'day');
  }

  return dates;
}

/**
 * Accepts an array of date ranges and returns an array of all dates for the given ranges.
 */
export function getAllDatesForAllRanges(
  dateRanges: { startDate: string; endDate: string }[]
): Set<string> {
  // Get all dates within each range
  const dateList = dateRanges.reduce(
    (
      accumulator: string[],
      dateRange: { startDate: string; endDate: string }
    ) => {
      return accumulator.concat(
        getDatesWithinRange(dateRange.startDate, dateRange.endDate)
      );
    },
    []
  );

  return new Set(dateList);
}

/**
 * Validates the given start and end dates to ensure that the end date is not before the start date,
 * and that both dates are not older than the current date.
 */
export function validateDates(startDate: Date, endDate: Date): boolean {
  assertIsDate(startDate);
  assertIsDate(endDate);

  const currentDate = new Date();

  // Check if end date is before start date
  if (endDate < startDate) {
    return false;
  }

  // Check if start date is before current date
  if (currentDate > startDate) {
    return false;
  }

  // Check if end date is before current date
  if (currentDate > endDate) {
    return false;
  }

  return true;
}

/**
 * Accepts a range of dates with the minimum night stays required for the dates within that range and
 * returns the dates within that range and associates the minimum nights required for each individual date.
 */
export function getMinStaysWithinRange(
  startDate: string,
  endDate: string,
  numberOfNights: number
): { date: string; minNights: number }[] {
  const dates = getDatesWithinRange(startDate, endDate);
  const minArray = dates.map((x) => {
    return { date: x, minNights: numberOfNights };
  });

  return minArray;
}

/**
 * Accepts a list of ranges of dates with the minimum night stays required for the dates within each range
 * Applies the getMinStaysWithinRange function to get min nights for each date within that range
 */
export function getMinStays(
  listOfMinRanges: {
    startDate: string;
    endDate: string;
    numberOfNights: number;
  }[]
): { date: string; minNights: number }[] {
  // list of dates with a minStay
  const minNights = listOfMinRanges.reduce(
    (
      accumulator: { date: string; minNights: number }[],
      object: {
        startDate: string;
        endDate: string;
        numberOfNights: number;
      }
    ) =>
      accumulator.concat(
        getMinStaysWithinRange(
          object.startDate,
          object.endDate,
          object.numberOfNights
        )
      ),
    []
  );
  return minNights;
}

/**
 * Accepts a list of dates and associated minimum nights
 * Returns the minimum night requirement for a given date
 */
export function getMinStayForSelectedDate(
  minNights: { date: Date; minNights: number }[],
  checkIn: Date
): number {
  assertIsDate(checkIn);

  return (
    minNights.find((dateWithMinNightStay) =>
      dayjs(checkIn).isSame(dateWithMinNightStay.date, 'day')
    )?.minNights || 1
  );
}

export const currentDate = () => new Date();

/**
 * Calculates the difference in days between two dates.
 */
export const diffDates = (date1: Date, date2: Date) => {
  assertIsDate(date1);
  assertIsDate(date2);

  return dayjs(date2).diff(dayjs(date1), 'day');
};

/**
 * Format a date to a human readable format
 * Example: Monday, January 1, 2021
 */
export function formatDateHumanReadable(
  date: Date,
  excludeYear: boolean = false
): string {
  return date.toLocaleDateString('en-US', {
    weekday: 'long',
    year: excludeYear ? undefined : 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

/**
 * Format a date to a human readable format
 * Example: Mon, January 1, 2021
 */
export const formatStringDateHumanReadable = (
  datetime: string,
  excludeYear: boolean = false
) => {
  const options: Intl.DateTimeFormatOptions = {
    weekday: 'short',
    year: excludeYear ? undefined : 'numeric',
    month: 'long',
    day: 'numeric',
    timeZone: 'UTC',
  };

  const parsedDatetime = new Date(datetime);
  return parsedDatetime.toLocaleDateString('en-US', options);
};

/**
 * Format a date to a human readable format.
 * Example: Jan 1
 *
 * Uses UTC parsing for string inputs to avoid server/client timezone
 * mismatch during SSR hydration (server runs in UTC, client in local TZ).
 */
export const formatDateMMMD = (date: Date | dayjs.Dayjs | string) => {
  if (typeof date === 'string') {
    return dayjs.utc(date).format('MMM D');
  }
  if (date instanceof Date) {
    return dayjs.utc(date).format('MMM D');
  }
  return dayjs.utc(date.valueOf()).format('MMM D');
};

/**
 * Format a date to a human readable format
 * Example:  2021-01-01
 */
export const formatDateYYYYMMDD = (date: Date | dayjs.Dayjs | string) => {
  return dayjs(date).format('YYYY-MM-DD');
};

export function formatDateDiff(
  startDate: string,
  endDate: string,
  maxUnits: number = 2
): string {
  const end = new Date(endDate);
  const start = new Date(startDate);
  const secondsInMillisecond = 1000;
  const minutesInSecond = 60;
  const hoursInMinute = 60;
  const daysInHour = 24;

  const isPositive = end >= start;
  const millisecondsDiff = isPositive
    ? end.getTime() - start.getTime()
    : start.getTime() - end.getTime();

  // const milliseconds = millisecondsDiff % secondsInMillisecond;
  const seconds =
    Math.floor(millisecondsDiff / secondsInMillisecond) % minutesInSecond;
  const minutes =
    Math.floor(millisecondsDiff / (secondsInMillisecond * minutesInSecond)) %
    hoursInMinute;
  const hours =
    Math.floor(
      millisecondsDiff /
        (secondsInMillisecond * minutesInSecond * hoursInMinute)
    ) % daysInHour;
  const days = Math.floor(
    millisecondsDiff /
      (secondsInMillisecond * minutesInSecond * hoursInMinute * daysInHour)
  );
  const years = end.getFullYear() - start.getFullYear();

  const timeUnits = [
    { unit: 'year', value: Math.abs(years) },
    { unit: 'day', value: Math.abs(days) },
    { unit: 'hour', value: Math.abs(hours) },
    { unit: 'minute', value: Math.abs(minutes) },
    { unit: 'second', value: Math.abs(seconds) },
    // { unit: 'ms', value: Math.abs(milliseconds) }
  ];

  const nonZeroUnits = timeUnits.filter((unit) => unit.value !== 0);
  const selectedUnits = nonZeroUnits.slice(0, maxUnits);
  const formattedDiff = selectedUnits
    .map(
      (unit) =>
        `${unit.value} ${unit.value !== 1 ? unit.unit + 's' : unit.unit}`
    )
    .join(', ');

  if (isPositive) {
    if (selectedUnits.length === 0) {
      return 'less than 1 second';
    } else {
      return formattedDiff;
    }
  } else {
    return formattedDiff + ' ago';
  }
}
